/**
 * POS grid layout v2 helpers (cells, categoryOrder, groups).
 */

import { resolveProductCategoryId } from './posProduct'

export const GRID_LAYOUT_VERSION = 2

export const TILE_TYPES = ['category', 'product', 'group', 'preset']

/** Placeholder refId for the draggable “New group” picker clone before placement is confirmed. */
export const NEW_GROUP_TEMPLATE_REF = '__NEW__'

export function isNewGroupTemplate (tile) {
  return tile?.type === 'group' && (
    tile.refId === NEW_GROUP_TEMPLATE_REF || tile._isNewGroupTemplate === true
  )
}

/**
 * Grid indices reserved for back / prev / next when drilling into a category or group.
 * Matches ProductCategoryGrid and useRegisterGridDrill.
 */
export function getGridNavSlotIndices (rows, cols) {
  const total = rows * cols
  return [0, total - 2, total - 1]
}

export function isGridNavSlotIndex (index, rows, cols) {
  const total = rows * cols
  return index === 0 || index === total - 2 || index === total - 1
}

export function isGridBackSlotIndex (index) {
  return index === 0
}

export function isGridPrevSlotIndex (index, rows, cols) {
  return index === rows * cols - 2
}

export function isGridNextSlotIndex (index, rows, cols) {
  return index === rows * cols - 1
}

/** Content cell indices when only the back control (slot 0) is reserved. */
export function buildStaticDrillContentSlotIndices (rows, cols) {
  const total = rows * cols
  const slots = []
  for (let i = 1; i < total; i++) slots.push(i)
  return slots
}

/** Content cell indices available when nav controls occupy corners of the grid. */
export function buildGridContentSlotIndices (rows, cols) {
  const total = rows * cols
  const nav = new Set(getGridNavSlotIndices(rows, cols))
  const slots = []
  for (let i = 0; i < total; i++) {
    if (!nav.has(i)) slots.push(i)
  }
  return slots
}

/**
 * Normalize category/group spatial cells: clear nav slots and relocate any tiles stored there.
 */
export function normalizeDrillSpatialCells (cells, rows, cols) {
  const total = rows * cols
  const contentSlots = buildGridContentSlotIndices(rows, cols)
  const nav = new Set(getGridNavSlotIndices(rows, cols))
  const src = Array.isArray(cells) ? cells.slice(0, total) : []
  while (src.length < total) src.push(null)

  const out = Array.from({ length: total }, () => null)
  const orphans = []

  for (let i = 0; i < total; i++) {
    const cell = src[i] ? normalizeTileRef(src[i]) : null
    if (!cell) continue
    if (nav.has(i)) orphans.push(cell)
    else out[i] = cell
  }

  let slotIdx = 0
  for (const orphan of orphans) {
    while (slotIdx < contentSlots.length && out[contentSlots[slotIdx]]) slotIdx++
    if (slotIdx < contentSlots.length) {
      out[contentSlots[slotIdx]] = orphan
      slotIdx++
    }
  }

  return out
}

function upgradeItemsToContentCells (items, rows, cols) {
  const contentSlots = buildGridContentSlotIndices(rows, cols)
  const total = rows * cols
  const cells = Array.from({ length: total }, () => null)
  const normalized = normalizeItemsList(items)
  for (let i = 0; i < normalized.length && i < contentSlots.length; i++) {
    cells[contentSlots[i]] = normalized[i]
  }
  return cells
}

/**
 * @typedef {{ type: string, refId: string|number, name?: string, color?: string|null, icon?: string|null, price?: number|null, image_url?: string|null }} GridTileRef
 * @typedef {{ version: number, rows: number, cols: number, cells: Array<GridTileRef|null>, categoryOrder?: Record<string, { cells: Array<GridTileRef|null> }>, groups?: Record<string, { name: string, color?: string|null, icon?: string|null, cells: Array<GridTileRef|null> }> }} GridLayout
 */

export function generateGroupId () {
  const rand = Math.random().toString(36).slice(2, 10)
  return `grp_${rand}`
}

export function generatePresetId () {
  const rand = Math.random().toString(36).slice(2, 10)
  return `pst_${rand}`
}

/** Minimal product tile ref; display fields are resolved from the catalog at runtime. */
export function toProductTileRef (product) {
  const id = Number(product?.id ?? product?.refId)
  if (!Number.isFinite(id) || id <= 0) return null
  return { type: 'product', refId: id }
}

/** @param {import('./gridLayout').GridLayout|null|undefined} layout */
export function getPresetMeta (layout, presetId) {
  if (!layout?.presets || presetId == null) return null
  const meta = layout.presets[String(presetId)]
  if (!meta || typeof meta !== 'object') return null
  const productId = Number(meta.product_id)
  if (!Number.isFinite(productId) || productId <= 0) return null
  const mode = meta.convenience_fee_mode
  if (mode !== 'apply' && mode !== 'exempt') return null
  const displayName = String(meta.display_name || '').trim()
  if (!displayName) return null
  return {
    product_id: productId,
    display_name: displayName,
    convenience_fee_mode: mode
  }
}

export function findTreeNodeById (nodes, id) {
  const targetId = Number(id)
  for (const n of nodes || []) {
    if (Number(n.id) === targetId) return n
    if (n.children?.length) {
      const found = findTreeNodeById(n.children, id)
      if (found) return found
    }
  }
  return null
}

export function upgradeLayoutV1ToV2 (layout) {
  const base = layout && typeof layout === 'object' ? { ...layout } : {}
  return {
    ...base,
    version: GRID_LAYOUT_VERSION,
    categoryOrder: base.categoryOrder && typeof base.categoryOrder === 'object'
      ? { ...base.categoryOrder }
      : {},
    groups: base.groups && typeof base.groups === 'object'
      ? { ...base.groups }
      : {},
    presets: base.presets && typeof base.presets === 'object'
      ? { ...base.presets }
      : {}
  }
}

export function normalizeLayout (raw) {
  const incoming = raw && typeof raw === 'object' ? raw : {}
  const rows = Math.max(1, Math.min(8, Number(incoming.rows) || 4))
  const cols = Math.max(1, Math.min(8, Number(incoming.cols) || 4))
  const total = rows * cols
  const cells = normalizeCellsArray(
    Array.isArray(incoming.cells) ? incoming.cells : [],
    total
  )

  let layout = {
    version: Number(incoming.version) || 1,
    rows,
    cols,
    cells,
    categoryOrder: {},
    groups: {},
    presets: {}
  }

  if (layout.version < GRID_LAYOUT_VERSION) {
    layout = upgradeLayoutV1ToV2(layout)
  }

  if (incoming.categoryOrder && typeof incoming.categoryOrder === 'object') {
    layout.categoryOrder = normalizeCategoryOrderMap(incoming.categoryOrder, rows, cols)
  }

  if (incoming.groups && typeof incoming.groups === 'object') {
    layout.groups = normalizeGroupsMap(incoming.groups, rows, cols)
  }

  if (incoming.presets && typeof incoming.presets === 'object') {
    layout.presets = normalizePresetsMap(incoming.presets)
  }

  return layout
}

function normalizeTileRef (tile) {
  if (!tile || typeof tile !== 'object') return null
  const type = String(tile.type || '')
  if (!TILE_TYPES.includes(type)) return null
  if (tile.refId === undefined || tile.refId === null || tile.refId === '') return null
  const out = {
    type,
    refId: (type === 'group' || type === 'preset') ? String(tile.refId) : Number(tile.refId)
  }
  // Product display (name/price) is resolved at runtime from the catalog index.
  if (type !== 'product') {
    if (tile.name != null) out.name = String(tile.name)
    if (tile.price !== undefined) out.price = tile.price
  }
  if (tile.color !== undefined) out.color = tile.color
  if (tile.icon !== undefined) out.icon = tile.icon
  return out
}

function normalizeItemsList (items) {
  if (!Array.isArray(items)) return []
  return items.map(normalizeTileRef).filter(Boolean)
}

function normalizeSpatialCellsEntry (value, rows, cols) {
  const total = rows * cols
  if (!value || typeof value !== 'object') {
    return normalizeDrillSpatialCells([], rows, cols)
  }
  if (Array.isArray(value.cells)) {
    return normalizeDrillSpatialCells(normalizeCellsArray(value.cells, total), rows, cols)
  }
  if (Array.isArray(value.items) && value.items.length) {
    return normalizeDrillSpatialCells(upgradeItemsToContentCells(value.items, rows, cols), rows, cols)
  }
  return normalizeDrillSpatialCells([], rows, cols)
}

function normalizeCategoryOrderEntry (value, rows, cols) {
  const total = rows * cols
  if (!value || typeof value !== 'object') {
    return { pages: [] }
  }
  if (Array.isArray(value.pages) && value.pages.length) {
    return {
      pages: value.pages.map((page) => ({
        cells: normalizeDrillSpatialCells(
          normalizeCellsArray(Array.isArray(page?.cells) ? page.cells : [], total),
          rows,
          cols
        )
      }))
    }
  }
  if (Array.isArray(value.cells)) {
    return {
      pages: [{
        cells: normalizeDrillSpatialCells(normalizeCellsArray(value.cells, total), rows, cols)
      }]
    }
  }
  if (Array.isArray(value.items) && value.items.length) {
    return {
      pages: [{
        cells: normalizeDrillSpatialCells(
          upgradeItemsToContentCells(value.items, rows, cols),
          rows,
          cols
        )
      }]
    }
  }
  return { pages: [] }
}

function normalizeCategoryOrderMap (map, rows, cols) {
  const out = {}
  for (const [key, value] of Object.entries(map)) {
    if (!value || typeof value !== 'object') continue
    out[String(key)] = normalizeCategoryOrderEntry(value, rows, cols)
  }
  return out
}

function normalizeCellsArray (cells, total) {
  const src = Array.isArray(cells) ? cells.slice(0, total) : []
  const out = []
  for (let i = 0; i < total; i++) {
    const tile = src[i]
    out[i] = tile ? normalizeTileRef(tile) : null
  }
  return out
}

function normalizeGroupsMap (map, rows, cols) {
  const out = {}
  for (const [key, value] of Object.entries(map)) {
    if (!value || typeof value !== 'object') continue
    out[String(key)] = {
      name: String(value.name || 'Group'),
      color: value.color ?? null,
      icon: value.icon ?? null,
      cells: normalizeSpatialCellsEntry(value, rows, cols)
    }
  }
  return out
}

function normalizePresetsMap (map) {
  const out = {}
  for (const [key, value] of Object.entries(map)) {
    if (!value || typeof value !== 'object') continue
    const productId = Number(value.product_id)
    if (!Number.isFinite(productId) || productId <= 0) continue
    const displayName = String(value.display_name || '').trim()
    if (!displayName) continue
    const mode = value.convenience_fee_mode
    if (mode !== 'apply' && mode !== 'exempt') continue
    out[String(key)] = {
      product_id: productId,
      display_name: displayName,
      convenience_fee_mode: mode
    }
  }
  return out
}

function spatialEntryHasContent (entry) {
  return (entry?.cells || []).some(Boolean)
}

/** Pages of spatial cells for a categoryOrder entry (legacy single cells → one page). */
export function getCategoryOrderPages (entry) {
  if (!entry || typeof entry !== 'object') return []
  if (Array.isArray(entry.pages) && entry.pages.length) {
    return entry.pages.map(p => p?.cells || [])
  }
  if (Array.isArray(entry.cells)) return [entry.cells]
  return []
}

export function getCategoryOrderPageCount (layout, categoryId) {
  const pages = getCategoryOrderPages(layout?.categoryOrder?.[String(categoryId)])
  return Math.max(1, pages.length || 0)
}

export function categoryOrderEntryHasContent (entry) {
  return getCategoryOrderPages(entry).some(page => (page || []).some(Boolean))
}

/**
 * Editor nav page ceiling: stored custom pages or catalog default pages, whichever is larger.
 * Lets operators page to empty slots while editing; save paths collapse trailing empties.
 */
export function getCategoryEditorNavPageCount (entry, defaultPageCount) {
  const storedCount = categoryOrderEntryHasContent(entry)
    ? getCategoryOrderPages(entry).length
    : 0
  const defaultCount = Math.max(0, Number(defaultPageCount) || 0)
  return Math.max(1, storedCount, defaultCount)
}

/**
 * Update one page within a categoryOrder entry (returns normalized entry).
 */
export function setCategoryOrderPage (entry, pageIndex, cells, rows, cols) {
  const pages = getCategoryOrderPages(entry).map(p => [...(p || [])])
  const idx = Math.max(0, Number(pageIndex) || 0)
  while (pages.length <= idx) pages.push([])
  pages[idx] = normalizeDrillSpatialCells(cells || [], rows, cols)

  let lastWithContent = -1
  for (let i = pages.length - 1; i >= 0; i--) {
    if ((pages[i] || []).some(Boolean)) {
      lastWithContent = i
      break
    }
  }
  if (lastWithContent < 0) return { pages: [] }

  return {
    pages: pages.slice(0, lastWithContent + 1).map(cellsArr => ({ cells: cellsArr }))
  }
}

/** Stable identity for comparing spatial cells (ignores display-only fields). */
export function spatialCellRefKey (cell) {
  if (!cell) return null
  return `${cell.type}:${cell.refId}`
}

/** Whether two spatial cell arrays represent the same tile placement. */
export function spatialCellsEqual (a, b) {
  const len = Math.max(a?.length || 0, b?.length || 0)
  for (let i = 0; i < len; i++) {
    if (spatialCellRefKey(a?.[i]) !== spatialCellRefKey(b?.[i])) return false
  }
  return true
}

/**
 * Remove a category's custom drill-down layout from a grid layout object.
 * @returns {import('./gridLayout').GridLayout} new layout reference
 */
export function removeCategoryOrderEntry (layout, categoryId) {
  const id = String(categoryId)
  const existing = layout?.categoryOrder
  if (!existing || !(id in existing)) {
    return layout
  }
  const rest = { ...existing }
  delete rest[id]
  return { ...layout, categoryOrder: rest }
}

/**
 * Category ids referenced by category tiles on the root grid.
 */
export function referencedRootCategoryIds (cells) {
  const set = new Set()
  for (const cell of cells || []) {
    if (cell?.type === 'category' && cell.refId != null) {
      set.add(String(cell.refId))
    }
  }
  return set
}

/**
 * @param {{ getCategoryById?: (id: number) => object|null, getProductById?: (id: number) => object|null, groupExists?: (id: string) => boolean }} catalog
 */
export function pruneLayout (layout, catalog = {}) {
  const normalized = normalizeLayout(layout)
  const getCategoryById = catalog.getCategoryById || (() => null)
  const getProductById = catalog.getProductById || (() => null)
  const groupExists = catalog.groupExists || ((id) => Boolean(normalized.groups?.[id]))
  const presetExists = catalog.presetExists || ((id) => Boolean(getPresetMeta(normalized, id)))

  const cells = normalized.cells.map((cell) => {
    if (!cell) return null
    return isTileRefValid(cell, { getCategoryById, getProductById, groupExists, presetExists }) ? cell : null
  })

  const categoryOrder = {}
  for (const [catId, entry] of Object.entries(normalized.categoryOrder || {})) {
    const prunedPages = getCategoryOrderPages(entry).map((pageCells) => {
      const catCells = (pageCells || []).map((cell) => {
        if (!cell) return null
        if (cell.type !== 'product' && cell.type !== 'category' && cell.type !== 'preset') return null
        return isTileRefValid(cell, { getCategoryById, getProductById, groupExists: () => true, presetExists })
          ? cell
          : null
      })
      return normalizeDrillSpatialCells(catCells, normalized.rows, normalized.cols)
    }).filter(page => page.some(Boolean))

    if (prunedPages.length) {
      categoryOrder[catId] = {
        pages: prunedPages.map(cells => ({ cells }))
      }
    }
  }

  const groups = {}
  for (const [groupId, group] of Object.entries(normalized.groups || {})) {
    const groupCells = (group?.cells || []).map((cell) => {
      if (!cell) return null
      if (cell.type !== 'product' && cell.type !== 'category' && cell.type !== 'preset') return null
      return isTileRefValid(cell, { getCategoryById, getProductById, groupExists: () => true, presetExists })
        ? cell
        : null
    })
    groups[groupId] = {
      name: group.name || 'Group',
      color: group.color ?? null,
      icon: group.icon ?? null,
      cells: normalizeDrillSpatialCells(groupCells, normalized.rows, normalized.cols)
    }
  }

  // Drop groups not referenced on root grid and with no cells (optional cleanup)
  const referencedGroupIds = new Set(
    cells.filter(c => c?.type === 'group').map(c => String(c.refId))
  )
  for (const id of Object.keys(groups)) {
    if (!referencedGroupIds.has(id) && !spatialEntryHasContent(groups[id])) {
      delete groups[id]
    }
  }

  const presets = {}
  const referencedPresetIds = new Set()
  const visitPresetRef = (cell) => {
    if (cell?.type === 'preset' && cell.refId != null) {
      referencedPresetIds.add(String(cell.refId))
    }
  }
  for (const cell of cells) visitPresetRef(cell)
  for (const entry of Object.values(categoryOrder)) {
    for (const page of entry?.pages || []) {
      for (const cell of page?.cells || []) visitPresetRef(cell)
    }
  }
  for (const group of Object.values(groups)) {
    for (const cell of group?.cells || []) visitPresetRef(cell)
  }

  for (const [presetId, preset] of Object.entries(normalized.presets || {})) {
    const meta = getPresetMeta({ presets: { [presetId]: preset } }, presetId)
    if (!meta) continue
    const p = getProductById(meta.product_id)
    if (p?.archived) continue
    if (!referencedPresetIds.has(String(presetId))) continue
    presets[String(presetId)] = meta
  }

  return {
    ...normalized,
    version: GRID_LAYOUT_VERSION,
    cells,
    categoryOrder,
    groups,
    presets
  }
}

export function normalizeProductRefId (refId) {
  const id = Number(refId)
  return Number.isFinite(id) && id > 0 ? id : null
}

/** Minimal product records for tiles already placed on a layout (catalog may be incomplete). */
export function collectLayoutProductStubs (layout) {
  const stubs = []
  const seen = new Set()

  const visit = (cell) => {
    const id = normalizeProductRefId(cell?.refId)
    if (!cell || cell.type !== 'product' || id == null || seen.has(id)) return
    seen.add(id)
    stubs.push({
      id,
      name: cell.name,
      price: cell.price,
      archived: false
    })
  }

  for (const cell of layout?.cells || []) visit(cell)
  for (const entry of Object.values(layout?.categoryOrder || {})) {
    for (const page of getCategoryOrderPages(entry)) {
      for (const cell of page || []) visit(cell)
    }
  }
  for (const group of Object.values(layout?.groups || {})) {
    for (const cell of group?.cells || []) visit(cell)
  }
  for (const preset of Object.values(layout?.presets || {})) {
    const id = normalizeProductRefId(preset?.product_id)
    if (id == null || seen.has(id)) continue
    seen.add(id)
    stubs.push({
      id,
      name: preset.display_name,
      archived: false
    })
  }

  return stubs
}

/**
 * Union of product ids referenced on one or more grid layouts (root, categoryOrder, groups, presets).
 * @param {object|Array<object>} layouts Map of scope → layout or array of layout objects
 * @returns {number[]}
 */
export function collectLayoutReferencedProductIds (layouts) {
  const ids = new Set()

  const visitLayout = (layout) => {
    for (const stub of collectLayoutProductStubs(layout || {})) {
      const id = normalizeProductRefId(stub?.id)
      if (id != null) ids.add(id)
    }
  }

  if (Array.isArray(layouts)) {
    for (const layout of layouts) visitLayout(layout)
  } else if (layouts && typeof layouts === 'object') {
    for (const layout of Object.values(layouts)) visitLayout(layout)
  }

  return [...ids]
}

/** Merge several product lists into a numeric-id lookup for grid save/prune. */
export function buildProductCatalogLookup (...sources) {
  const map = new Map()
  for (const source of sources) {
    for (const product of source || []) {
      const id = normalizeProductRefId(product?.id ?? product?.refId)
      if (id == null) continue
      map.set(id, product)
    }
  }
  return (id) => {
    const normalized = normalizeProductRefId(id)
    if (normalized == null) return null
    return map.get(normalized) || null
  }
}

function isTileRefValid (tile, { getCategoryById, getProductById, groupExists, presetExists }) {
  if (!tile?.type || tile.refId === undefined || tile.refId === null) return false
  if (tile.type === 'category') {
    const cat = getCategoryById(Number(tile.refId))
    return Boolean(cat)
  }
  if (tile.type === 'product') {
    const id = normalizeProductRefId(tile.refId)
    if (id == null) return false
    const p = getProductById(id)
    if (p) return !p.archived
    // Keep explicit layout placements when the local catalog is incomplete.
    return true
  }
  if (tile.type === 'group') {
    if (tile.refId === NEW_GROUP_TEMPLATE_REF || tile._isNewGroupTemplate) return false
    return groupExists(String(tile.refId))
  }
  if (tile.type === 'preset') {
    return presetExists(String(tile.refId))
  }
  return false
}

/**
 * Products visible in POS category drill-down (mirrors API for_pos_grid rules).
 * @param {number|string} categoryId
 * @param {Array<object>} products
 * @param {(id: number) => object|null} getCategoryById
 */
export function filterProductsForPosGridCategory (categoryId, products, getCategoryById) {
  const cat = getCategoryById?.(Number(categoryId))
  const targetId = Number(categoryId)
  const inCategory = (products || []).filter(
    p => resolveProductCategoryId(p) === targetId
  )
  if (cat?.include_all_products_in_grid) return inCategory
  return inCategory.filter(p => p.include_in_grid)
}

/**
 * Build default ordered items for a category (subcategories by sort_order, then products by name).
 * Used when opening category editor with no saved categoryOrder.
 * @param {(id: number) => object|null} [getCategoryById] when provided, filters products via for_pos_grid rules
 */
export function buildDefaultCategoryItems (categoryId, categoryTree, products = [], getCategoryById = null) {
  const node = findTreeNodeById(categoryTree, categoryId)
  const items = []

  const children = [...(node?.children || [])].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
  )
  for (const c of children) {
    items.push({
      type: 'category',
      refId: c.id,
      name: c.name
    })
  }

  const catProducts = (getCategoryById
    ? filterProductsForPosGridCategory(categoryId, products, getCategoryById)
    : (products || []).filter(p => resolveProductCategoryId(p) === Number(categoryId))
  )
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))

  for (const p of catProducts) {
    items.push({
      type: 'product',
      refId: p.id,
      name: p.name,
      price: p.price
    })
  }

  return items
}

/**
 * Build default spatial cells for a category (subcategories then products, packed sequentially).
 */
export function buildDefaultCategoryCells (categoryId, categoryTree, products = [], rows = 4, cols = 4, getCategoryById = null) {
  const items = buildDefaultCategoryItems(categoryId, categoryTree, products, getCategoryById)
  return upgradeItemsToContentCells(items, rows, cols)
}

/**
 * Build default spatial cell pages for a category (subcategories + products, paginated).
 */
export function buildDefaultCategoryPages (categoryId, categoryTree, products = [], rows = 4, cols = 4, getCategoryById = null) {
  const items = buildDefaultCategoryItems(categoryId, categoryTree, products, getCategoryById)
  const slotsPerPage = buildGridContentSlotIndices(rows, cols).length
  if (!items.length) {
    return [upgradeItemsToContentCells([], rows, cols)]
  }
  const pages = []
  for (let i = 0; i < items.length; i += slotsPerPage) {
    pages.push(upgradeItemsToContentCells(items.slice(i, i + slotsPerPage), rows, cols))
  }
  return pages
}

/** Default spatial cells for one page of a category layout. */
export function buildDefaultCategoryPageCells (categoryId, categoryTree, products = [], rows = 4, cols = 4, getCategoryById = null, pageIndex = 0) {
  const pages = buildDefaultCategoryPages(categoryId, categoryTree, products, rows, cols, getCategoryById)
  const idx = Math.max(0, Number(pageIndex) || 0)
  return pages[idx] || pages[0] || upgradeItemsToContentCells([], rows, cols)
}

/**
 * Resolve saved categoryOrder items into enriched drill tiles (sync enrichment via callbacks).
 * @deprecated Use resolveCategoryCells for spatial category layouts.
 */
export function resolveCategoryOrderItems (items, enrichTile) {
  if (!Array.isArray(items)) return []
  return items
    .map((ref) => enrichTile(ref))
    .filter(Boolean)
}

function resolveSpatialCellsFromCells (layout, cells, enrichTile) {
  const rows = Math.max(1, Number(layout?.rows) || 4)
  const cols = Math.max(1, Number(layout?.cols) || 4)
  const total = rows * cols
  const saved = normalizeDrillSpatialCells(cells || [], rows, cols)
  return Array.from({ length: total }, (_, i) => {
    const ref = saved[i] || null
    return ref ? enrichTile(ref) : null
  })
}

function resolveSpatialCells (layout, entry, enrichTile) {
  if (!entry) return resolveSpatialCellsFromCells(layout, [], enrichTile)
  return resolveSpatialCellsFromCells(layout, entry.cells || [], enrichTile)
}

/**
 * Resolve category drill cells from layout.categoryOrder (enriched, padded to rows × cols).
 * @param {number} [pageIndex] page within multi-page categoryOrder
 */
export function resolveCategoryCells (layout, categoryId, enrichTile, pageIndex = 0) {
  const entry = layout?.categoryOrder?.[String(categoryId)]
  const pages = getCategoryOrderPages(entry)
  if (!pages.length) {
    return resolveSpatialCellsFromCells(layout, [], enrichTile)
  }
  const idx = Math.max(0, Math.min(Number(pageIndex) || 0, pages.length - 1))
  return resolveSpatialCellsFromCells(layout, pages[idx] || [], enrichTile)
}

/**
 * Resolve group spatial cells from layout.groups (enriched, padded to rows × cols).
 */
export function resolveGroupCells (layout, groupId, enrichTile) {
  const group = layout?.groups?.[String(groupId)]
  return resolveSpatialCells(layout, group, enrichTile)
}

/** @deprecated Use resolveGroupCells for spatial group layouts. */
export function resolveGroupOrderItems (layout, groupId, enrichTile) {
  return resolveGroupCells(layout, groupId, enrichTile).filter(Boolean)
}

/**
 * Check whether category has a custom spatial layout override in layout.
 */
export function hasCategoryOrderOverride (layout, categoryId) {
  const entry = layout?.categoryOrder?.[String(categoryId)]
  return categoryOrderEntryHasContent(entry)
}

/**
 * Get categoryOrder cells for editor/runtime for one page (returns null if no override).
 */
export function getCategoryOrderCells (layout, categoryId, pageIndex = 0) {
  const entry = layout?.categoryOrder?.[String(categoryId)]
  if (!categoryOrderEntryHasContent(entry)) return null
  const pages = getCategoryOrderPages(entry)
  const idx = Math.max(0, Math.min(Number(pageIndex) || 0, pages.length - 1))
  return (pages[idx] || []).map((cell) => (cell ? { ...cell } : null))
}

/** @deprecated Use getCategoryOrderCells. */
export function getCategoryOrderItems (layout, categoryId) {
  const cells = getCategoryOrderCells(layout, categoryId)
  if (!cells) return null
  return cells.filter(Boolean).map((cell) => ({ ...cell }))
}

export function getGroupMeta (layout, groupId) {
  return layout?.groups?.[String(groupId)] || null
}

/**
 * Collect all refIds placed on root cells for deduping pickers.
 */
export function collectRootRefIds (cells) {
  const set = new Set()
  for (const cell of cells || []) {
    if (cell?.refId != null) set.add(cell.refId)
  }
  return set
}

/**
 * Collect refIds placed in a spatial cell array (root or group grid).
 */
export function collectCellRefIds (cells) {
  return collectRootRefIds(cells)
}

/**
 * Build editor cellsFull structure from a flat cells array.
 */
export function cellsToEditorCellsFull (cells) {
  return (cells || []).map((cell) => (cell ? [JSON.parse(JSON.stringify(cell))] : []))
}

/**
 * Serialize editor cellsFull back to a flat cells array.
 */
export function editorCellsFullToCells (cellsFull) {
  return (cellsFull || []).map((list) => (list?.[0] ? { ...list[0] } : null))
}

/**
 * Build editor tree nodes from layout for Option 5 navigator.
 */
export function buildEditorTreeNodes (layout, { getCategoryById, getGroupById, getPresetById, getProductById } = {}) {
  const nodes = [{ id: 'root', kind: 'root', label: 'Root grid', children: [] }]
  const rootChildren = nodes[0].children

  for (const cell of layout?.cells || []) {
    if (!cell) continue
    if (cell.type === 'category') {
      const cat = getCategoryById?.(Number(cell.refId))
      rootChildren.push({
        id: `cat-${cell.refId}`,
        kind: 'category',
        refId: Number(cell.refId),
        label: cat?.name || cell.name || `Category ${cell.refId}`,
        children: []
      })
    } else if (cell.type === 'group') {
      const grp = getGroupById?.(String(cell.refId))
      rootChildren.push({
        id: `grp-${cell.refId}`,
        kind: 'group',
        refId: String(cell.refId),
        label: grp?.name || cell.name || 'Group',
        children: []
      })
    } else if (cell.type === 'preset') {
      const preset = getPresetById?.(String(cell.refId))
      rootChildren.push({
        id: `pst-${cell.refId}`,
        kind: 'preset',
        refId: String(cell.refId),
        label: preset?.display_name || cell.name || 'Preset',
        children: []
      })
    } else if (cell.type === 'product') {
      const product = getProductById?.(Number(cell.refId))
      rootChildren.push({
        id: `prod-${cell.refId}`,
        kind: 'product',
        refId: Number(cell.refId),
        label: product?.name || cell.name || `Product ${cell.refId}`,
        children: []
      })
    }
  }

  return nodes
}

/**
 * Swap two editor cell lists (each length 0 or 1).
 */
export function swapEditorCells (cellsFull, indexA, indexB) {
  if (!Array.isArray(cellsFull)) return
  const a = cellsFull[indexA] || []
  const b = cellsFull[indexB] || []
  const itemA = a[0] ? { ...a[0] } : null
  const itemB = b[0] ? { ...b[0] } : null
  a.splice(0, a.length)
  b.splice(0, b.length)
  if (itemB) a.push(itemB)
  if (itemA) b.push(itemA)
}
